import type { ReactElement } from "react";
import { VERWALTUNGS_BEREICHE } from "./Verwaltung.js";
import type { VerwaltungsBereich } from "./Verwaltung.js";
import "./VerwaltungsSpalte.css";
import { KiKnopf } from "../components/meHuellen.js";

/**
 * Das Untermenü der Verwaltungsbereiche — in der SPALTE des Rahmens (Etappe 2, Aufgabe 1).
 *
 * Bis hierher trug die Verwaltung ihre zehn Bereiche als Tab-Leiste über dem Inhalt
 * (`vw__unternav` in Verwaltung.tsx). Zehn Reiter passen in keine Zeile: Sie brachen um,
 * schoben den Inhalt nach unten und sahen mit jedem weiteren Bereich schlechter aus. In der
 * Spalte stehen sie untereinander — dort, wo in jedem anderen Bereich die Liste steht, aus
 * der man weiterblättert (Muster Etappe 1) —, und der Inhalt bekommt die volle Breite.
 *
 * Gezeichnet wird ausschliesslich aus `VERWALTUNGS_BEREICHE`: Die Tabelle bleibt die EINE
 * Quelle (der Router liest sie ebenso), und ein neuer Bereich steht ohne Zutun in der
 * Spalte — sie ist die einzige Navigation zu ihm.
 *
 * **Das Untermenü ist alles, was hier steht** (Entscheid Dirk, 06.09.2026). Zwischen
 * Etappe 2, Aufgabe 2 und diesem Entscheid klappte ein Bereich mit Objektliste sie als
 * ZWEITE EBENE unter seinem Eintrag auf (`zweiteEbene`). Am Live-System hat sich das nicht
 * bewährt: Die Objektzeilen wiederholten, was rechts ohnehin zu sehen war, und in der
 * schmalen Spalte schoben sich Name und Marke einer Zeile übereinander. Die Objektlisten
 * stehen seither wieder im INHALT (views/verwaltung/<bereich>.tsx, zusammengesetzt von
 * `ObjektBereich`), und die Prop ist ersatzlos entfallen — samt ihrem CSS-Block
 * `vws__zweite-ebene`.
 */
export type VerwaltungsSpalteProps = {
  /** Der offene Bereich — `null` heisst „noch keiner gewählt" (Adresse `/verwaltung`). */
  bereich: VerwaltungsBereich | null;
  onBereich: (b: VerwaltungsBereich) => void;
  /**
   * Der Weg zurück dorthin, WOHER man in die Verwaltung kam (Etappe 2, Aufgabe 3) — heute
   * genau ein Fall: „Pflege in der Verwaltung" aus dem Artikelkatalog.
   *
   * Er steht HIER und nicht nur im Kopf des iPhone-Einspalters, seit der Artikelstamm eine
   * Objektebene hat: Ohne gewähltes Objekt zeigt der Einspalter die SPALTE, und über der
   * Spalte zeichnet der Rahmen keinen Rückweg — dort ist man normalerweise schon am
   * Ursprung (siehe MeSchale). Mit einer fremden Herkunft stimmt das nicht, und der Weg
   * zurück in den Katalog wäre sonst gar nicht gehbar.
   *
   * Ohne fremde Herkunft gibt es ihn nicht: Das Untermenü ist der Ursprung.
   *
   * Seit dem 06.09.2026 zeigt der Einspalter auf `/verwaltung/artikel` wieder den INHALT
   * (die Objektliste steht dort), und über dem Inhalt zeichnet der Rahmen seinen eigenen
   * Rückweg. Dieser Knopf hier bleibt trotzdem — er ist der einzige auf dem SCHREIBTISCH:
   * Breit gibt es den Kopf-Knopf des Rahmens gar nicht, die Spalte steht dauerhaft daneben.
   */
  rueckweg?: { beschriftung: string; aktion: () => void };
  /** Nur diese Bereiche zeigen (Zuschnitt „belegdesign": Unternehmen und Layout).
   *  Fehlt die Angabe, stehen alle da. Kosmetik, kein Sicherheitsmerkmal. */
  nurBereiche?: readonly VerwaltungsBereich[];
};

export function VerwaltungsSpalte(p: VerwaltungsSpalteProps): ReactElement {
  return (
    <nav className="vws" aria-label="Verwaltungsbereiche">
      {p.rueckweg !== undefined && (
        <KiKnopf art="leise" className="vws__rueckweg" onClick={p.rueckweg.aktion}>
          {p.rueckweg.beschriftung}
        </KiKnopf>
      )}
      <ul className="vws__liste">
        {VERWALTUNGS_BEREICHE.filter((b) => p.nurBereiche === undefined
          || p.nurBereiche.includes(b.id)).map((b) => {
          const aktiv = b.id === p.bereich;
          return (
            <li key={b.id} className="vws__punkt">
              <button
                type="button"
                className={`vws__eintrag${aktiv ? " vws__eintrag--aktiv" : ""}`}
                aria-current={aktiv ? "page" : undefined}
                /* Auch der aktive Eintrag meldet: Auf dem iPhone steht die Spalte VOR dem
                   Inhalt (siehe spalteZuerst in App.tsx) — ein Antippen ist dort der Weg
                   zurück in den offenen Bereich, nicht eine Wiederholung ohne Wirkung. */
                onClick={() => p.onBereich(b.id)}
              >
                {b.label}
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
